import React, { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Card,
  ResourceList,
  ResourceItem,
  Stack,
  ButtonGroup,
  Button,
  Text,
  Spinner,
  Layout,
  Select,
  TextField,
  Checkbox,
  Toast,
  Frame,
  Modal,
  TextContainer,
  ProgressBar,
  Thumbnail,
  Badge,
  Banner,
  FormLayout,
  TextStyle
} from '@shopify/polaris';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ImageProcessingService from '../services/imageProcessingService';
import { useShop } from '../contexts/ShopContext';
import { useTemplate } from '../contexts/TemplateContext';
import { useAuth } from '../contexts/AuthContext';
import { saveImageSettings, getImageSettings } from '../services/imageSettingsService';
import axios from 'axios';
import { ImageService } from '../services/imageService';
import { hasEnoughCredits } from '../services/creditService';
import { deductCredits, refundCredits, recordPushToShopify } from '../services/optimizedCreditService';
import { getServerUrl } from '../config';
import { updateStatistics, formatBytes, getShopStatistics } from '../services/statisticsService';
import ShopifyBulkOperationsService from '../services/shopifyBulkOperationsService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Fix the Firebase db import to use the correct named export

function BulkEditImages() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentShop } = useShop();
  const { user } = useAuth();
  const { credits } = useTemplate();
  
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({
    processed: 0,
    total: 0,
    uploaded: 0,
    failed: 0,
    showProgress: false,
    currentOperation: '',
    currentProgress: 0
  });
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [format, setFormat] = useState('webp');
  const [compressionLevel, setCompressionLevel] = useState('medium');
  const [maxWidth, setMaxWidth] = useState('');
  const [maxHeight, setMaxHeight] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [imageSizes, setImageSizes] = useState({});
  const [useSeoNaming, setUseSeoNaming] = useState(false);
  const [imagesToDelete, setImagesToDelete] = useState(new Set());
  const [selectedImage, setSelectedImage] = useState(null);
  const [showLargeImageModal, setShowLargeImageModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [toastProps, setToastProps] = useState({ content: '', error: false });
  const [processedImages, setProcessedImages] = useState([]);
  const [totalSavings, setTotalSavings] = useState({
    originalSize: 0,
    processedSize: 0,
    bytesSaved: 0,
    compressionRatio: 0
  });
  const [shopStats, setShopStats] = useState(null);
  const [processedImageIds, setProcessedImageIds] = useState(new Set());
  const [successfulProducts] = useState(new Map());
  const [processedProductIds, setProcessedProductIds] = useState(new Set());
  const [uiLocked, setUiLocked] = useState(false);
  const [userGuidanceMessage, setUserGuidanceMessage] = useState('Select images and click "Process Images" to begin optimization');
  
  useEffect(() => {
    const productIds = searchParams.get('ids')?.split(',') || [];
    console.log('Product IDs from URL:', productIds);
    console.log('Shop info:', { currentShop });
    console.log('Auth info:', { user });
    
    if (!currentShop) {
      return;
    }
    
    if (productIds.length > 0) {
      fetchProducts(productIds);
    } else {
      setLoading(false);
      if (!currentShop) {
        setToastError(true);
        setToastMessage('Shop information not available');
        setShowToast(true);
      }
    }
  }, [searchParams, currentShop, user]);

  useEffect(() => {
    // Wait for authentication to load
    if (!user) {
      return;
    }

    // Redirect to login if not authenticated
    if (!user) {
      navigate('/app/login', { 
        state: { 
          from: window.location.pathname + window.location.search,
          message: 'Please sign in to access the bulk image editor'
        } 
      });
      return;
    }

    const productIds = searchParams.get('ids')?.split(',') || [];
    console.log('Product IDs from URL:', productIds);
    console.log('Shop info:', { currentShop });
    console.log('Auth info:', { user });
    
    if (!currentShop) {
      return;
    }
    
    if (productIds.length > 0) {
      fetchProducts(productIds);
    } else {
      setLoading(false);
      if (!currentShop) {
        setToastError(true);
        setToastMessage('Shop information not available');
        setShowToast(true);
      }
    }
  }, [searchParams, currentShop, user, navigate]);

  useEffect(() => {
    const loadSettings = async () => {
      if (currentShop?.id) {
        try {
          const settings = await getImageSettings(currentShop.id);
          setFormat(settings.format);
          setCompressionLevel(settings.compressionLevel);
          setMaxWidth(settings.maxWidth || '');
          setMaxHeight(settings.maxHeight || '');
          setUseSeoNaming(settings.useSeoNaming || false);
        } catch (error) {
          console.error('Error loading settings:', error);
          // Use defaults if loading fails
          setFormat('webp');
          setCompressionLevel('medium');
          setMaxWidth('');
          setMaxHeight('');
          setUseSeoNaming(false);
        }
      }
    };

    loadSettings();
  }, [currentShop?.id]);

  useEffect(() => {
    const loadProcessedState = async () => {
      if (currentShop?.shop) {
        try {
          // Clear any existing processed image state first
          await ImageProcessingService.clearProcessedImageState(currentShop.shop);
          console.log('Cleared existing processed image state');

          const state = await ImageProcessingService.loadProcessedImageState(currentShop.shop);
          if (state?.processedImages?.length) {
            // Only load images that belong to currently selected products
            const productIds = searchParams.get('ids')?.split(',') || [];
            const filteredImages = state.processedImages.filter(img => {
              // Skip invalid images
              if (!img?.originalImage?.product_id) {
                console.warn('Skipping invalid image in processed state:', img);
                return false;
              }
              return productIds.includes(img.originalImage.product_id.toString());
            });
            
            if (filteredImages.length > 0) {
              console.log('Loaded processed images:', filteredImages.length);
              setProcessedImages(filteredImages);
            } else {
              console.log('No matching processed images found for current products');
              setProcessedImages([]);
            }
          } else {
            console.log('No processed images in state');
            setProcessedImages([]);
          }
        } catch (error) {
          console.error('Error loading processed state:', error);
          setProcessedImages([]);
        }
      }
    };

    loadProcessedState();
  }, [currentShop, searchParams]);

  useEffect(() => {
    const loadShopStatistics = async () => {
      if (currentShop?.id) {
        try {
          const stats = await getShopStatistics(currentShop.id);
          setShopStats(stats);
        } catch (error) {
          console.error('Error loading shop statistics:', error);
        }
      }
    };

    loadShopStatistics();
  }, [currentShop?.id]);

  useEffect(() => {
    if (processedImages && processedImages.length > 0) {
      // Create a new Set to track processed product IDs
      const newProcessedProductIds = new Set();
      
      // Extract product IDs from all processed images
      processedImages.forEach(item => {
        if (item?.originalImage?.product_id) {
          newProcessedProductIds.add(item.originalImage.product_id);
        }
      });
      
      console.log('Updating processedProductIds from processedImages:', {
        processedImagesCount: processedImages.length,
        newProcessedProductIdsCount: newProcessedProductIds.size,
        newProcessedProductIds: Array.from(newProcessedProductIds)
      });
      
      // Update the state with the new set of processed product IDs
      setProcessedProductIds(newProcessedProductIds);
      
      // Also filter selectedItems to remove any processed products
      setSelectedItems(prevSelected => {
        const filteredSelection = prevSelected.filter(itemId => !newProcessedProductIds.has(itemId));
        return filteredSelection;
      });
    }
  }, [processedImages]);

  useEffect(() => {
    if (processedProductIds.size > 0) {
      // Force remove any processed products from selection
      setSelectedItems(prevSelected => {
        const filteredSelection = prevSelected.filter(itemId => !processedProductIds.has(itemId));
        
        // Log if we had to remove any items
        if (filteredSelection.length !== prevSelected.length) {
          console.log('Forced removal of processed products from selection', {
            before: prevSelected,
            after: filteredSelection,
            processedIds: Array.from(processedProductIds)
          });
        }
        
        return filteredSelection;
      });
    }
  }, [processedProductIds]);

  const fetchProducts = async (productIds = null) => {
    if (!currentShop?.shop || !currentShop?.accessToken) {
      setToastError(true);
      setToastMessage('Shop information not available');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('Fetching products with GraphQL:', { productIds, shop: currentShop.shop });
      
      const bulkService = new ShopifyBulkOperationsService(
        currentShop.shop,
        currentShop.accessToken
      );
      
      const query = ShopifyBulkOperationsService.buildFullProductQueryForIds(productIds);
      let bulkOperation;
      
      try {
        bulkOperation = await bulkService.startBulkProductsQuery(query);
      } catch (error) {
        // If there's already a bulk operation running, wait for it to complete
        if (error.message.includes('already in progress')) {
          console.log('Bulk operation already in progress, waiting for completion...');
          // Extract operation ID from error message
          const match = error.message.match(/gid:\/\/shopify\/BulkOperation\/\d+/);
          if (match) {
            const operationId = match[0];
            console.log('Waiting for operation:', operationId);
            const results = await bulkService.pollBulkOperation(operationId);
            if (results) {
              bulkOperation = { id: operationId };
            } else {
              throw new Error('Failed to get results from existing bulk operation');
            }
          } else {
            throw new Error('Could not extract operation ID from error message');
          }
        } else {
          throw error;
        }
      }
      
      if (!bulkOperation?.id) {
        throw new Error('Failed to start bulk operation - no operation ID received');
      }
      
      const jsonlData = await bulkService.pollBulkOperation(bulkOperation.id);
      console.log('Raw results from API:', jsonlData);
      
      if (!jsonlData || typeof jsonlData !== 'string' || jsonlData.trim() === '') {
        console.error('No valid JSONL data from bulk operation');
        setSelectedProducts([]);
        setToastError(true);
        setToastMessage('No products found - please check the product IDs and try again');
        return;
      }
      
      // Process the JSONL data to get an array of products
      const results = bulkService.processProductsData(jsonlData);
      
      if (!results || !Array.isArray(results) || results.length === 0) {
        console.error('No valid results after processing bulk operation data');
        setSelectedProducts([]);
        setToastError(true);
        setToastMessage('No products found - please check the product IDs and try again');
        return;
      }

      // Validate that we have actual product data
      const validProducts = results.filter(product => 
        product && 
        product.id && 
        product.title &&
        product.id.includes('gid://shopify/Product/')
      );

      if (validProducts.length === 0) {
        console.error('No valid product nodes found in results');
        setSelectedProducts([]);
        setToastError(true);
        setToastMessage('No valid products found in the results');
        return;
      }

      // Process products to deduplicate images and maintain variant relationships
      const processedProducts = await Promise.all(validProducts.map(async product => {
        // Create a map of unique images using src as key
        const uniqueImagesMap = new Map();
        
        // Track which variants use which images
        const imageVariantMap = new Map();

        // Format images from GraphQL response
        const images = product.images?.map((img, index) => ({
          id: img.id,
          src: img.url,
          alt: img.altText || '',
          width: img.width,
          height: img.height,
          position: index + 1, // Use array index + 1 as position since it's not available in GraphQL
          variant_ids: []
        })) || [];

        // Process all variants and their image relationships
        product.variants?.forEach(variant => {
          const variantImage = variant.image ? 
            images.find(img => img.id === variant.image.id) : null;
          
          if (variantImage) {
            // Add to unique images if not already present
            if (!uniqueImagesMap.has(variantImage.src)) {
              uniqueImagesMap.set(variantImage.src, {
                ...variantImage,
                variant_ids: []
              });
            }
            // Add variant relationship
            const imageEntry = uniqueImagesMap.get(variantImage.src);
            imageEntry.variant_ids.push(variant.id);
          }
        });

        // Add any product images that aren't linked to variants and fetch their metadata
        await Promise.all(images.map(async image => {
          if (!uniqueImagesMap.has(image.src)) {
            try {
              // Fetch image metadata if not already available
              let size = null;
              if (!image.width || !image.height) {
                const response = await fetch(image.src, { method: 'HEAD' });
                size = response.headers.get('content-length');
              }
              
              uniqueImagesMap.set(image.src, {
                ...image,
                variant_ids: [],
                size: size ? parseInt(size, 10) : null
              });
            } catch (error) {
              console.warn('Could not fetch image metadata:', error);
              uniqueImagesMap.set(image.src, {
                ...image,
                variant_ids: []
              });
            }
          }
        }));

        return {
          ...product,
          images: Array.from(uniqueImagesMap.values()),
          _originalImages: images // Keep original images for reference
        };
      }));

      console.log('Processed products with GraphQL:', processedProducts);
      setSelectedProducts(processedProducts);
    } catch (error) {
      console.error('Error fetching products with GraphQL:', error);
      setToastError(true);
      setToastMessage('Failed to fetch products: ' + error.message);
      setSelectedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert compression level to quality value
  const getQualityFromLevel = (level) => {
    switch (level) {
      case 'low': return 60;
      case 'medium': return 80;
      case 'high': return 90;
      default: return 80;
    }
  };

  // Helper function to generate SEO-friendly filenames
  const getSeoFilename = (title, index) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
      .concat(index > 0 ? `-${index}` : ''); // Add number suffix if needed
  };

  const handleProcessImages = async () => {
    setProcessing(true);
    setUiLocked(true); // Lock the UI while processing
    setProgress(prev => ({ ...prev, showProgress: false }));

    // Get all images from selected products
    const images = [];
    let productImageCounts = new Map(); // Track images per product for credit check
    
    selectedProducts.forEach(product => {
      if (selectedItems.includes(product.id)) {
        const productImages = product.images?.filter(img => !imagesToDelete.has(img.id)) || [];
        productImageCounts.set(product.id, productImages.length);
        images.push(...productImages);
      }
    });

    if (images.length === 0) {
      setToastError(true);
      setToastMessage('No images selected for processing');
      setShowToast(true);
      return;
    }

    // Check if we have enough credits by making a real-time check to Firebase
    const creditsNeeded = images.length; // Now we need one credit per image
    try {
      const hasCredits = await hasEnoughCredits(user.uid, creditsNeeded);
      if (!hasCredits) {
        setToastError(true);
        setToastMessage(`Not enough credits. You need ${creditsNeeded} credits (1 per image). Please purchase more credits to continue.`);
        setShowToast(true);
        return;
      }
    } catch (error) {
      console.error('Error checking credits:', error);
      setToastError(true);
      setToastMessage('Error checking credits. Please try again.');
      setShowToast(true);
      return;
    }

    const uniqueImageSources = new Set();
    const imagesToProcess = selectedProducts
      .filter(product => selectedItems.includes(product.id))
      .flatMap(product => product.images?.filter(image => {
        // Only include images that are not marked for deletion and haven't been processed
        if (image && image.src && !imagesToDelete.has(image.id) && !uniqueImageSources.has(image.src)) {
          uniqueImageSources.add(image.src);
          return true;
        }
        return false;
      }) || []);

    if (!imagesToProcess || imagesToProcess.length === 0) {
      setToastError(true);
      setToastMessage('No images found to process');
      setShowToast(true);
      return;
    }

    // Update progress state with correct total
    setProgress(prev => ({
      ...prev,
      total: imagesToProcess.length,
      processed: 0,
      uploaded: 0,
      failed: 0,
      showProgress: true,
      currentOperation: 'Starting image processing...',
      currentProgress: 0
    }));

    try {
      await processImages(imagesToProcess);
    } catch (error) {
      console.error('Error in handleProcessImages:', error);
      setToastError(true);
      setToastMessage('Error processing images: ' + error.message);
      setShowToast(true);
      setProcessing(false);
      setUiLocked(false); // Unlock the UI if there's an error
    }
  };

  const processImages = useCallback(async (images) => {
    if (!images || !Array.isArray(images) || images.length === 0) {
      setToastError(true);
      setToastMessage('No images to process');
      setShowToast(true);
      return;
    }

    if (!currentShop?.shop) {
      setToastError(true);
      setToastMessage('Shop information not available');
      setShowToast(true);
      return;
    }

    // Initialize progress state with correct total
    setProgress(prevProgress => ({
      ...prevProgress,
      total: images.length,
      processed: 0,
      successful: 0,
      failed: 0,
      showProgress: true,
      currentOperation: 'Starting image processing...',
      currentProgress: 0
    }));
    setProcessing(true);

    // Reset processed images for the new batch
    setProcessedImages([]);

    const errors = [];
    const maxConsecutiveErrors = 3;
    let consecutiveErrors = 0;
    let lastErrorTime = 0;
    let localProcessedCount = 0;
    let localSuccessCount = 0;
    let localFailedCount = 0;
    
    try {
      // Create processing options from component settings
      const processingOptions = {
        format: format, // Use selected format
        quality: getQualityFromLevel(compressionLevel),
        maxWidth: maxWidth ? parseInt(maxWidth) : null,
        maxHeight: maxHeight ? parseInt(maxHeight) : null,
        maxRetries: 3,
        retryDelay: 2000,
        useSeoNaming,
        productTitle: selectedProducts[0]?.title || '',
        getSeoFilename
      };

      // Track processed images to prevent duplicates
      const processedImageIds = new Set();
      
      // Process all selected images
      const processedResults = [];
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        
        // Skip if this image has already been processed
        if (processedImageIds.has(image.id)) {
          console.log('Skipping already processed image:', image.id);
          continue;
        }
        
        if (!image?.src) {
          console.error('Invalid image object:', image);
          errors.push({ image: 'unknown', error: 'Invalid image: missing source URL' });
          localProcessedCount++;
          localFailedCount++;
          setProgress(prev => ({
            ...prev,
            processed: localProcessedCount,
            failed: localFailedCount,
            errors: [...prev.errors, { image: 'unknown', error: 'Invalid image: missing source URL' }]
          }));
          continue;
        }

        try {
          // Add fixed delay between images (except for the first one)
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay between images
          }

          const shopifyMetadata = {
            id: image.id,
            product_id: image.product_id || selectedProducts.find(p => p.images?.some(img => img.id === image.id))?.id,
            position: image.position,
            alt: image.alt,
            width: image.width,
            height: image.height,
            src: image.src,
            variant_ids: image.variant_ids || [],
            admin_graphql_api_id: image.admin_graphql_api_id
          };

          console.log('Processing image:', { src: image.src, metadata: shopifyMetadata });
          const result = await processImage(image.src, shopifyMetadata, processingOptions);
          console.log('Image processed successfully:', result);

          // Mark this image as processed
          processedImageIds.add(image.id);

          // Update total savings
          if (result.originalSize && result.processedSize && result.bytesSaved) {
            setTotalSavings(prev => {
              const newSavings = {
                originalSize: (prev.originalSize || 0) + result.originalSize,
                processedSize: (prev.processedSize || 0) + result.processedSize,
                bytesSaved: (prev.bytesSaved || 0) + result.bytesSaved,
                compressionRatio: ((((prev.bytesSaved || 0) + result.bytesSaved) / ((prev.originalSize || 0) + result.originalSize)) * 100).toFixed(2)
              };

              // Update progress message with current savings
              const savingsText = newSavings.bytesSaved >= 0 ? 
                `Saved ${formatBytes(newSavings.bytesSaved)} (${newSavings.compressionRatio}% reduction)` :
                `Size increased by ${formatBytes(Math.abs(newSavings.bytesSaved))} (${Math.abs(newSavings.compressionRatio)}% increase)`;
              
              setProgress(p => ({
                ...p,
                currentOperation: savingsText
              }));

              return newSavings;
            });
          }

          // Add processed image info and track size savings
          processedResults.push({
            originalImage: shopifyMetadata,
            processedImage: {
              ...result,
              originalSize: parseInt(result.originalSize) || 0,
              processedSize: parseInt(result.processedSize) || 0,
              bytesSaved: parseInt(result.bytesSaved) || 0
            }
          });

          // Store successful product for statistics
          if (!successfulProducts.has(shopifyMetadata.product_id)) {
            successfulProducts.set(shopifyMetadata.product_id, { images: [] });
          }
          successfulProducts.get(shopifyMetadata.product_id).images.push({
            originalSize: parseInt(result.originalSize) || 0,
            processedSize: parseInt(result.processedSize) || 0,
            bytesSaved: parseInt(result.bytesSaved) || 0,
            jobId: result.jobId
          });

          // Update progress for successful processing
          localProcessedCount++;
          localSuccessCount++;
          setProgress(prev => ({
            ...prev,
            processed: localProcessedCount,
            uploaded: localSuccessCount,
            currentProgress: Math.round(localProcessedCount / images.length * 100),
            currentOperation: localProcessedCount === (images.length * 2) ? `✅ Finished!` : `Processed ${localProcessedCount} of ${images.length} images, saved ${formatBytes(totalSavings.bytesSaved)} (${totalSavings.compressionRatio}% reduction)`
          }));

          // Reset consecutive errors after success
          consecutiveErrors = 0;
          lastErrorTime = 0;

        } catch (error) {
          console.error('Error processing image:', error);
          consecutiveErrors++;
          lastErrorTime = Date.now();
          errors.push({ image: image.src, error: error.message });

          // Update progress for failed processing
          localProcessedCount++;
          localFailedCount++;
          setProgress(prev => ({
            ...prev,
            failed: localFailedCount,
            processed: localProcessedCount,
            currentProgress: Math.round(localProcessedCount / images.length * 100),
            currentOperation: `Processed ${localProcessedCount} of ${images.length} images, saved ${formatBytes(totalSavings.bytesSaved)} (${totalSavings.compressionRatio}% reduction)`
          }));

          // Handle rate limiting and consecutive errors
          if (error.message.includes('429') || error.message.toLowerCase().includes('too many requests')) {
            const waitTime = 10000; // Wait 10 seconds when hitting rate limit
            console.log(`Rate limit hit, waiting ${waitTime/1000} seconds...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            
            // Retry the failed image
            i--; // Decrease counter to retry this image
            continue;
          }

          // Check for consecutive errors
          if (consecutiveErrors >= maxConsecutiveErrors) {
            const errorMessage = `Stopping after ${maxConsecutiveErrors} consecutive errors. Last error: ${error.message}`;
            console.error(errorMessage);
            errors.push({ image: 'system', error: errorMessage });
            break;
          }
        }
      }

      // Update processed images state
      setProcessedImages(processedResults);
      
      // Save processed state - MERGE with existing state
      if (processedResults.length > 0) {
        try {
          // Load current state first to ensure we're appending, not overwriting
          const currentState = await ImageProcessingService.loadProcessedImageState(currentShop.shop);
          const currentProcessedImages = currentState?.processedImages || [];
          
          // Merge existing processed images with new ones
          const mergedProcessedImages = [...currentProcessedImages];
          
          // Add new processed results, avoiding duplicates
          processedResults.forEach(result => {
            // Check if this image is already in the processed images
            const existingIndex = mergedProcessedImages.findIndex(img => 
              img.originalImage?.id === result.originalImage?.id);
            
            if (existingIndex >= 0) {
              // Update existing entry
              mergedProcessedImages[existingIndex] = result;
              console.log('Updated existing processed image:', result.originalImage?.id);
            } else {
              // Add new entry
              mergedProcessedImages.push(result);
              console.log('Added new processed image:', result.originalImage?.id);
            }
          });
          
          console.log(`Saving ${mergedProcessedImages.length} processed images (${currentProcessedImages.length} existing + ${processedResults.length} new)`);
          
          // Save the merged state
          await ImageProcessingService.saveProcessedImageState(currentShop.shop, {
            processedImages: mergedProcessedImages,
            shopifyMetadata: currentState?.shopifyMetadata || {}
          });
          
          // Update local state
          setProcessedImages(mergedProcessedImages);
        } catch (error) {
          console.error('Error saving processed image state:', error);
        }
      }

      setToastProps({
        content: `Successfully processed ${localSuccessCount} images${localFailedCount > 0 ? `, ${localFailedCount} failed` : ''}`,
        error: localFailedCount > 0
      });

    } catch (error) {
      console.error('Error in image processing:', error);
      setToastProps({
        content: error.message || 'Failed to process images',
        error: true
      });
    } finally {
      setProcessing(false);
      // Don't unlock the UI after processing - keep it locked until push to Shopify
      // setUiLocked(false); // Removed to keep the success banner visible
      // Make sure processedImages state is up to date
      const savedState = await ImageProcessingService.loadProcessedImageState(currentShop.shop);
      if (savedState?.processedImages) {
        setProcessedImages(savedState.processedImages);
        
        // Update processedProductIds based on the processed images
        // Group images by product_id to determine which products have all images processed
        const productImagesMap = new Map();
        
        // First, collect all images by product
        selectedProducts.forEach(product => {
          if (selectedItems.includes(product.id)) {
            const productImages = product.images?.filter(img => !imagesToDelete.has(img.id)) || [];
            productImagesMap.set(product.id, {
              total: productImages.length,
              processed: 0
            });
          }
        });
        
        // Count processed images for each product
        savedState.processedImages.forEach(item => {
          if (item?.originalImage?.product_id) {
            const productId = item.originalImage.product_id;
            const productStats = productImagesMap.get(productId);
            if (productStats) {
              productStats.processed += 1;
            }
          }
        });
        
        // Mark products as processed only when all their images are processed
        const newProcessedProductIds = new Set(processedProductIds);
        productImagesMap.forEach((stats, productId) => {
          if (stats.total > 0 && stats.processed >= stats.total) {
            newProcessedProductIds.add(productId);
            console.log(`Marking product ${productId} as fully processed: ${stats.processed}/${stats.total} images`);
          } else {
            console.log(`Product ${productId} not fully processed: ${stats.processed}/${stats.total} images`);
          }
        });
        
        console.log('Updated processedProductIds:', {
          before: processedProductIds.size,
          after: newProcessedProductIds.size,
          productIds: Array.from(newProcessedProductIds)
        });
        
        setProcessedProductIds(newProcessedProductIds);
      
      }
      
      // Final progress update with total savings
      if (processedImages.length > 0) {
        setProgress(prev => ({
          ...prev,
          currentOperation: `✅ Finished! Saved ${formatBytes(totalSavings.bytesSaved)} (${totalSavings.compressionRatio}% reduction)`,
          currentProgress: 100
        }));
      }
      // Clear selected images after processing
      setSelectedItems([]);
      setUserGuidanceMessage('Images processed successfully. Click "Push to Shopify" to upload your optimized images, or process additional products if needed.');
      // Removed toast message to avoid duplication with the banner
      // setToastMessage('Images processed successfully. Click "Push to Shopify" to continue.');
      // setToastError(false);
      // setShowToast(true);
    }
  }, [selectedProducts, imagesToDelete, currentShop, format, compressionLevel, maxWidth, maxHeight, useSeoNaming]);

  const processImage = async (imageUrl, shopifyMetadata, processingOptions) => {
    try {
      setProgress(prev => ({
        ...prev,
        currentImage: imageUrl
      }));

      const result = await ImageProcessingService.processImage(
        imageUrl,
        processingOptions,
        currentShop.shop,
        shopifyMetadata
      );

      return result;
    } catch (error) {
      console.error(`Error processing image ${imageUrl}:`, error);
      throw error;
    }
  };

  const handlePushToShopify = async () => {
    if (!currentShop?.accessToken || !currentShop?.shop) {
      setToastError(true);
      setToastMessage('Shop information is missing');
      setShowToast(true);
      return;
    }

    if (processedImages.length === 0) {
      setToastError(true);
      setToastMessage('No processed images to push');
      setShowToast(true);
      return;
    }

    // Count unique images for tracking purposes (no credit check needed anymore)
    const uniqueImageIds = new Set(
      processedImages.map(img => img.originalImage?.id)
    );
    console.log(`Pushing ${uniqueImageIds.size} processed images to Shopify`);

    setIsUploading(true);
    setUiLocked(true); // Lock UI during upload
    setProgress(prev => ({
      ...prev,
      processed: 0,
      total: processedImages.length * 2, // Double for the two-step process
      uploaded: 0,
      failed: 0,
      showProgress: true,
      currentOperation: 'Preparing to upload images...'
    }));

    let hasError = false;
    let localUploadedCount = 0;
    let localErrorCount = 0;

    try {
      // Use processed images if available, otherwise empty array
      const imagesToUpload = processedImages.length > 0 ? processedImages : [];
      
      console.log('Images to upload:', imagesToUpload);
      
      if (imagesToUpload.length === 0) {
        setToastError(true);
        setToastMessage('No images to process. Please process images first.');
        setShowToast(true);
        return;
      }
      
      setProgress({
        total: imagesToUpload.length,  
        processed: 0,
        uploaded: 0,
        failed: 0,
        currentOperation: 'Starting image processing...',
        currentProgress: 0,
        errors: [],
        showProgress: true
      });

      // Track successfully updated products for credit deduction
      const successfulProductIds = new Set();
      const successfulProducts = new Map(); // Track product data for credit deduction

      // First ensure we have the latest processed images state
      const savedState = await ImageProcessingService.loadProcessedImageState(currentShop.shop);
      const latestProcessedImages = savedState?.processedImages || [];
      
      // Create a map for quick lookup, handling both formats
      const processedImageMap = new Map(
        latestProcessedImages
          .map(item => {
            // Handle both formats
            const id = item.originalImage?.id || item.metadata?.shopifyMetadata?.id;
            if (!id) {
              console.warn('Skipping invalid image data:', item);
              return null;
            }

            // Convert processing result format to state format if needed
            const normalizedItem = item.originalImage ? item : {
              originalImage: item.metadata.shopifyMetadata,
              processedImage: {
                jobId: item.jobId,
                processedUrl: item.processedUrl,
                filename: item.metadata.options.filename,
                metadata: {
                  format: item.metadata.format || format,
                  width: item.metadata.width,
                  height: item.metadata.height,
                  size: item.metadata.size,
                  shopifyMetadata: item.metadata.shopifyMetadata
                },
                originalSize: item.metadata.originalSize,
                processedSize: item.metadata.processedSize,
                bytesSaved: item.metadata.bytesSaved,
                compressionRatio: item.metadata.compressionRatio,
                format: format // Use selected format
              },
              action: 'update'
            };

            return [id, normalizedItem];
          })
          .filter(Boolean)
      );

      // First pass: group images by product ID and deduplicate
      const uniqueImageMap = new Map(); // Use a Map to deduplicate images
      const productImagesMap = new Map(); // Initialize productImagesMap here
      const processedProductIds = new Set(); // Track processed product IDs
      const processedImageIds = new Set(); // Track processed image IDs
      const successfulImageIds = new Set(); // Track successfully processed image IDs
      
      // Debug information to understand the duplication issue
      console.log(`Starting with ${imagesToUpload.length} items in imagesToUpload array`);
      
      // Create a set of unique image IDs to track duplicates
      const uniqueImageIds = new Set();
      const duplicateImageIds = new Set();
      
      // First, extract product IDs from the selected products to use as fallback
      const productIdMap = new Map();
      selectedProducts.forEach(product => {
        if (product.id) {
          // Store product ID by title for later lookup
          productIdMap.set(product.title, product.id);
          // Also store without '(Copy)' suffix if present
          const titleWithoutCopy = product.title.replace(/ \(Copy\)$/, '');
          if (titleWithoutCopy !== product.title) {
            productIdMap.set(titleWithoutCopy, product.id);
          }
        }
      });
      
      console.log('Product ID Map:', Object.fromEntries(productIdMap));

      for (const item of imagesToUpload) {
        try {
          // Extract image and product IDs based on the data structure
          let imageId, productId, productTitle, originalImage, processedImageData;
          
          // Debug log with shorter output to avoid console clutter
          console.log('Processing item type:', item.originalImage ? 'Format 1' : 'Format 2');
          
          if (item.originalImage) {
            // Format 1: Has originalImage and processedImage structure
            imageId = item.originalImage.id;
            productId = item.originalImage.product_id;
            console.log('Format 1 - imageId:', imageId);
            productTitle = item.originalImage.alt || 'Unknown Product';
            originalImage = item.originalImage;
            processedImageData = item.processedImage;
            
            // If product_id is missing, try to extract it from other metadata
            if (!productId && productTitle) {
              // Try to get product ID from title mapping
              const productIdFromMap = productIdMap.get(productTitle);
              if (productIdFromMap) {
                productId = productIdFromMap;
                console.log('Format 1 - Found productId from title mapping:', productId);
              } else if (productIdMap[productTitle]) {
                // Try using bracket notation if Map.get() doesn't work
                productId = productIdMap[productTitle];
                console.log('Format 1 - Found productId from title mapping (bracket notation):', productId);
              }
            }
            
            // Store in the unique map using imageId as key
            if (imageId && productId && !uniqueImageMap.has(imageId)) {
              uniqueImageMap.set(imageId, { item, format: 'format1', productId, originalIndex: item.originalImage.position - 1 });
            }
          } else if (item.metadata?.shopifyMetadata) {
            // Format 2: Has metadata.shopifyMetadata structure
            imageId = item.metadata.shopifyMetadata.id;
            productId = item.metadata.shopifyMetadata.product_id;
            console.log('Format 2 - imageId:', imageId);
            
            // If product_id is missing, try to extract it from other metadata
            if (!productId) {
              // Try to find product ID in options
              const productIdFromOptions = item.metadata.options?.productId;
              if (productIdFromOptions) {
                productId = productIdFromOptions;
                console.log('Found productId in options:', productId);
              }
              
              // Try to get product ID from product title
              if (!productId && item.metadata.options?.productTitle) {
                const productIdFromMap = productIdMap.get(item.metadata.options.productTitle);
                if (productIdFromMap) {
                  productId = productIdFromMap;
                  console.log('Found productId from title mapping:', productId);
                }
              }
              
              // Try to extract from originalUrl
              if (!productId && item.originalUrl) {
                const urlMatch = item.originalUrl.match(/\/products\/([^\/]+)/);
                if (urlMatch && urlMatch[1]) {
                  productId = urlMatch[1];
                  console.log('Extracted productId from URL:', productId);
                }
              }
              
              // Last resort: extract product ID from image ID
              if (!productId && imageId && imageId.includes('gid://shopify/ProductImage/')) {
                // For Shopify, we can try to use the image ID to find the product
                // This is not ideal but better than failing
                const imageIdParts = imageId.split('/');
                if (imageIdParts.length > 0) {
                  // Use the first product ID from our map as a fallback
                  const firstProductId = [...productIdMap.values()][0];
                  if (firstProductId) {
                    productId = firstProductId;
                    console.log('Using fallback productId from map:', productId);
                  }
                }
              }
            }
            
            // Store in the unique map using imageId as key
            if (imageId && productId && !uniqueImageMap.has(imageId)) {
              uniqueImageMap.set(imageId, { item, format: 'format2', productId, originalIndex: item.metadata.shopifyMetadata.position - 1 });
            }
          } else {
            console.error('Unrecognized image data format:', item);
            localErrorCount++;
            setProgress(prev => ({
              ...prev,
              failed: localErrorCount,
              processed: localUploadedCount + localErrorCount,
              currentOperation: 'Error: Unrecognized image data format',
              errors: [...(prev.errors || []), { image: 'unknown', error: 'Unrecognized image data format' }]
            }));
            continue;
          }

          // Track duplicates for debugging
          if (uniqueImageIds.has(imageId)) {
            duplicateImageIds.add(imageId);
            console.log('Duplicate image detected:', imageId);
            continue; // Skip duplicates immediately
          } else {
            uniqueImageIds.add(imageId);
          }
          
          // Skip if we've already processed this image
          if (imageId && successfulImageIds.has(imageId)) {
            console.log('Skipping already processed image:', imageId);
            continue;
          }
          
          // Validate item has required data
          if (!imageId) {
            console.error('Invalid image data - missing imageId:', { imageId, item });
            localErrorCount++;
            setProgress(prev => ({
              ...prev,
              failed: localErrorCount,
              processed: localUploadedCount + localErrorCount,
              currentOperation: 'Error: Invalid image data',
              errors: [...(prev.errors || []), { image: 'unknown', error: 'Invalid image: missing imageId' }]
            }));
            continue;
          }

          // Ensure we have a valid product ID
          if (!productId) {
            // Try to get product ID from title mapping
            if (productTitle && productIdMap[productTitle]) {
              productId = productIdMap[productTitle];
              console.log('Found productId from title mapping:', productId);
            } else {
              console.error('Invalid image data - missing productId and could not find from title:', { imageId, productTitle, item });
              localErrorCount++;
              setProgress(prev => ({
                ...prev,
                failed: localErrorCount,
                processed: localUploadedCount + localErrorCount,
                currentOperation: 'Error: Missing product ID',
                errors: [...(prev.errors || []), { image: imageId, error: 'Invalid image: missing product ID' }]
              }));
              continue;
            }
          }

          // Add to the product group
          if (!productImagesMap.has(productId)) {
            productImagesMap.set(productId, []);
          }
          
          // Add this image to the product group
          productImagesMap.get(productId).push({
            imageId,
            productId,
            productTitle,
            originalImage,
            processedImageData,
            action: item.action === 'delete' || imagesToDelete.has(imageId) ? 'delete' : 'update',
            originalIndex: uniqueImageMap.get(imageId).originalIndex
          });
        } catch (error) {
          console.error('Error grouping image by product:', error);
          localErrorCount++;
          setProgress(prev => ({
            ...prev,
            failed: localErrorCount,
            processed: localUploadedCount + localErrorCount,
            currentOperation: `Error: ${error.message}`,
            errors: [...(prev.errors || []), error.message]
          }));
        }
      }
      
      // Debug information to understand the duplication issue
      console.log(`Found ${uniqueImageIds.size} unique images and ${duplicateImageIds.size} duplicates`);
      console.log(`Unique image map has ${uniqueImageMap.size} entries`);
      
      // Now group the deduplicated images by product ID for batch processing
      console.log(`Processing ${uniqueImageMap.size} unique images after deduplication`);
      
      // Update the progress total to reflect the actual number of unique images
      setProgress(prev => ({
        ...prev,
        total: uniqueImageIds.size,
        currentOperation: 'Processing deduplicated images...'
      }));
      
      // Track images by product to avoid duplicates during processing
      const processedImagesByProduct = new Map();
      
      // Convert the unique images map to the format needed for processing
      // First, create an array from the map entries to preserve original order
      const uniqueImagesArray = Array.from(uniqueImageMap.entries());
      
      // Clear the productImagesMap to ensure we don't have any duplicates from previous processing
      productImagesMap.clear();
      
      // Process images in their original order
      for (const [imageId, { item, format, productId, originalIndex }] of uniqueImagesArray) {
        try {
          // Initialize the product's image array if it doesn't exist
          if (!productImagesMap.has(productId)) {
            productImagesMap.set(productId, []);
          }
          
          let originalImage, processedImageData, productTitle;
          
          if (format === 'format1') {
            originalImage = item.originalImage;
            processedImageData = item.processedImage;
            productTitle = originalImage.alt || 'Unknown Product';
          } else { // format2
            productTitle = item.metadata.shopifyMetadata.alt || item.metadata.options?.productTitle || 'Unknown Product';
            
            // Convert to expected format
            originalImage = {
              ...item.metadata.shopifyMetadata,
              product_id: productId
            };
            
            // Ensure we have a valid processedUrl
            const processedUrl = item.processedUrl;
            if (!processedUrl) {
              console.warn(`Missing processedUrl for image ${imageId}, checking processedImageMap`);
              // Try to find it in the processedImageMap
              const mappedItem = processedImageMap.get(imageId);
              if (mappedItem && (mappedItem.processedImage?.processedUrl || mappedItem.processedUrl)) {
                console.log(`Found processedUrl in map for image ${imageId}`);
                processedImageData = {
                  processedUrl: mappedItem.processedImage?.processedUrl || mappedItem.processedUrl,
                  jobId: item.jobId || mappedItem.jobId,
                  format: item.metadata.format || format,
                  metadata: {
                    format: item.metadata.format || format,
                    width: item.metadata.width,
                    height: item.metadata.height,
                    size: item.metadata.size,
                    shopifyMetadata: item.metadata.shopifyMetadata
                  },
                  originalSize: item.metadata.originalSize,
                  processedSize: item.metadata.processedSize,
                  bytesSaved: item.metadata.bytesSaved,
                  compressionRatio: item.metadata.compressionRatio,
                  format: format
                };
              } else {
                console.error(`Cannot find processedUrl for image ${imageId} in any source`);
                throw new Error(`Missing processedUrl for image ${imageId}`);
              }
            } else {
              processedImageData = {
                processedUrl: processedUrl,
                jobId: item.jobId,
                format: item.metadata.format || format,
                metadata: {
                  format: item.metadata.format || format,
                  width: item.metadata.width,
                  height: item.metadata.height,
                  size: item.metadata.size,
                  shopifyMetadata: item.metadata.shopifyMetadata
                },
                originalSize: item.metadata.originalSize,
                processedSize: item.metadata.processedSize,
                bytesSaved: item.metadata.bytesSaved,
                compressionRatio: item.metadata.compressionRatio,
                format: format
              };
            }
          }
          
          // Add to the product group
          productImagesMap.get(productId).push({
            imageId,
            productId,
            productTitle,
            originalImage,
            processedImageData,
            action: item.action === 'delete' || imagesToDelete.has(imageId) ? 'delete' : 'update',
            originalIndex
          });
        } catch (error) {
          console.error('Error grouping image by product:', error);
          localErrorCount++;
          setProgress(prev => ({
            ...prev,
            failed: localErrorCount,
            processed: localUploadedCount + localErrorCount,
            currentOperation: `Error: ${error.message}`,
            errors: [...(prev.errors || []), error.message]
          }));
        }
      }
      
      // Process each product's images in batches
      for (const [productId, productImages] of productImagesMap.entries()) {
        // Sort images by their original index to preserve order
        productImages.sort((a, b) => a.originalIndex - b.originalIndex);
        
        console.log(`Processing ${productImages.length} images for product ${productId} (after deduplication)`);
        
        // Process each image for this product
        for (let i = 0; i < productImages.length; i++) {
          const imageInfo = productImages[i];
          const isLastImage = i === productImages.length - 1;
          const imageId = imageInfo.imageId || imageInfo.originalImage?.id;
          
          // Skip if we've already processed this image successfully
          if (imageId && successfulImageIds.has(imageId)) {
            console.log('Skipping already successfully processed image:', imageId);
            continue;
          }
          
          try {
            if (imageInfo.action === 'delete') {
              // Handle deletion using GraphQL
              const bulkService = new ShopifyBulkOperationsService(
                currentShop.shop,
                currentShop.accessToken
              );
              
              await bulkService.deleteProductImage(productId, imageInfo.imageId);
              
              successfulProductIds.add(productId);
              processedImageIds.add(imageInfo.imageId);
              processedProductIds.add(productId);
              successfulProducts.set(productId, {
                id: productId,
                title: imageInfo.productTitle,
                action: 'delete'
              });

              localUploadedCount++;
              setProgress(prev => ({
                ...prev,
                uploaded: localUploadedCount,
                processed: localUploadedCount + localErrorCount,
                currentOperation: `Deleted image ${localUploadedCount} of ${uniqueImageIds.size} images for ${productImagesMap.size} products`,
                currentProgress: Math.round((localUploadedCount + localErrorCount) / uniqueImageIds.size * 100)
              }));
            } else {
              // Check if we have a valid processed URL
              if (!imageInfo.processedImageData?.processedUrl) {
                console.warn(`Cannot find processed URL for image: ${imageInfo.imageId}, checking processedImageMap`);
                
                // Try to find the processed URL in the processedImageMap
                const processedItem = processedImageMap.get(imageInfo.imageId);
                if (processedItem && (processedItem.processedImage?.processedUrl || processedItem.processedUrl)) {
                  // Found the processed URL in the map, use it
                  imageInfo.processedImageData = imageInfo.processedImageData || {};
                  imageInfo.processedImageData.processedUrl = processedItem.processedImage?.processedUrl || processedItem.processedUrl;
                  console.log(`Found processed URL in map for image: ${imageInfo.imageId}`);
                  
                  // Also ensure we have the original image data
                  if (!imageInfo.originalImage && processedItem.originalImage) {
                    imageInfo.originalImage = processedItem.originalImage;
                  } else if (!imageInfo.originalImage && processedItem.metadata?.shopifyMetadata) {
                    // Create originalImage from metadata if available
                    imageInfo.originalImage = {
                      ...processedItem.metadata.shopifyMetadata,
                      product_id: productId
                    };
                  }
                } else {
                  // Still can't find a processed URL, report error
                  localErrorCount++;
                  setProgress(prev => ({
                    ...prev,
                    failed: localErrorCount,
                    processed: localUploadedCount + localErrorCount,
                    currentOperation: `Error: Cannot find processed URL for image ID ${imageInfo.imageId}`,
                    errors: [...(prev.errors || []), `Missing processed URL for image ID: ${imageInfo.imageId}`]
                  }));
                  continue;
                }
              }
              
              // Skip if we've already processed this image successfully
              if (imageId && successfulImageIds.has(imageId)) {
                console.log('Skipping already successfully processed image:', imageId);
                localUploadedCount++;
                setProgress(prev => ({
                  ...prev,
                  uploaded: localUploadedCount,
                  processed: localUploadedCount + localErrorCount,
                  currentOperation: `Skipped already processed image ${localUploadedCount} of ${uniqueImageIds.size} images for ${productImagesMap.size} products`,
                  currentProgress: Math.round((localUploadedCount + localErrorCount) / uniqueImageIds.size * 100)
                }));
                continue;
              }
              
              // Ensure we're using the selected format
              const processedImageWithFormat = {
                ...imageInfo.processedImageData,
                format: format, // Use selected format
                metadata: {
                  ...(imageInfo.processedImageData.metadata || {}),
                  format: format // Use selected format
                }
              };

              // Make sure we have all the original image data including position and variant_ids
              const originalImageData = {
                ...imageInfo.originalImage,
                position: imageInfo.originalImage.position || 1,
                alt: imageInfo.originalImage.alt || '',
                variant_ids: imageInfo.originalImage.variant_ids || []
              };

              // Create the image data object for the API call
              const imageData = {
                originalImage: {
                  ...originalImageData,
                  product_id: productId // Ensure product_id is set
                },
                processedImage: processedImageWithFormat,
                isLastImage: isLastImage // Set the flag to trigger batch processing on the last image
              };

              // Handle update with latest processed data
              try {
                const result = await ImageService.updateShopifyProductImage(
                  currentShop.shop,
                  currentShop.accessToken,
                  imageData
                );

                if (result.success) {
                  successfulProductIds.add(productId);
                  processedImageIds.add(imageInfo.imageId);
                  processedProductIds.add(productId);
                  successfulProducts.set(productId, {
                    id: productId,
                    title: imageInfo.productTitle,
                    action: 'update'
                  });
                  successfulImageIds.add(imageInfo.imageId);
                  localUploadedCount++;
                  setProgress(prev => ({
                    ...prev,
                    uploaded: localUploadedCount,
                    processed: localUploadedCount + localErrorCount,
                    currentOperation: `Processed ${localUploadedCount} of ${uniqueImageIds.size} images for ${productImagesMap.size} products`,
                    currentProgress: Math.round((localUploadedCount + localErrorCount) / uniqueImageIds.size * 100)
                  }));
                } else {
                  throw new Error(result.error || 'Unknown error');
                }
              } catch (updateError) {
                console.error('Error updating image:', updateError);
                localErrorCount++;
                setProgress(prev => ({
                  ...prev,
                  failed: localErrorCount,
                  processed: localUploadedCount + localErrorCount,
                  currentOperation: `Error: ${updateError.message}`,
                  errors: [...(prev.errors || []), updateError.message]
                }));
              }
            }
          } catch (error) {
            console.error('Error processing image:', error);
            localErrorCount++;
            hasError = true;
            setProgress(prev => ({
              ...prev,
              failed: localErrorCount,
              processed: localUploadedCount + localErrorCount,
              currentOperation: `Error: ${error.message}`,
              errors: [...(prev.errors || []), error.message]
            }));
          }
        }
      }

      // Update statistics for successful products
      if (successfulProductIds.size > 0) {
        try {
          // Use the accurate values from our UI state
          const batchStats = {
            imagesProcessed: progress.uploaded, // Use successful uploads count
            bytesOriginal: totalSavings.originalSize,
            bytesProcessed: totalSavings.processedSize,
            bytesSaved: totalSavings.bytesSaved
          };

          // Update statistics in Firestore
          await updateStatistics(currentShop.id, currentShop.userId, batchStats);

          // Log success
          console.log('Firebase Statistics Updated Successfully');

          // Clean up processed images after statistics calculation
          if (processedImageIds.size > 0) {
            try {
              await ImageProcessingService.cleanupProcessedImages(currentShop.id, Array.from(processedImageIds));
              console.log('Cleaned up processed images:', processedImageIds.size);
            } catch (cleanupError) {
              console.error('Error cleaning up processed images:', cleanupError);
              // Don't throw here as it's not critical
            }
          }

          // Record push history and deduct credits based on the total number of images
          const totalImagesProcessed = progress.uploaded; // Use the number of successfully uploaded images
          if (totalImagesProcessed > 0) {
            try {
              console.log(`Deducting ${totalImagesProcessed} credits for ${totalImagesProcessed} processed images`);
              
              // First deduct the credits using the optimized credit service
              const result = await deductCredits(user.uid, totalImagesProcessed, {
                type: 'image', // Use 'image' directly as the usage type for proper dashboard tracking
                shopId: currentShop.id,
                count: totalImagesProcessed,
                description: `Processed ${totalImagesProcessed} images`
              });
              
              // Import recordCreditUsage to properly track image credits for the pie chart
              // We'll use this to update the usageType without deducting additional credits
              if (result) {
                try {
                  // Import the function to update credit type metadata only
                  const { updateCreditUsageType } = await import('../services/metricsService');
                  
                  // Update the credit usage type without recording additional credits
                  await updateCreditUsageType(user.uid, currentShop.id, totalImagesProcessed, 'image', `Processed ${totalImagesProcessed} images`);
                  console.log(`Updated credit type for ${totalImagesProcessed} image credits`);
                } catch (recordError) {
                  console.error('Error updating image credit type:', recordError);
                  // Continue even if recording fails
                }
              }
              if (result) {
                console.log(`Successfully deducted ${totalImagesProcessed} credits`);
                
                // Record each product push to Shopify for metrics tracking
                // This ensures each product counts as 1 push in the metrics
                try {
                  const uniqueProductIds = Array.from(successfulProductIds);
                  console.log(`Recording ${uniqueProductIds.length} product pushes to Shopify metrics`);
                  
                  // Record each product push individually
                  for (const productId of uniqueProductIds) {
                    try {
                      // Use recordPushToShopify from optimizedCreditService
                      await recordPushToShopify(user.uid, currentShop.id, productId);
                      console.log(`Recorded push for product ${productId}`);
                    } catch (pushError) {
                      console.error(`Error recording push for product ${productId}:`, pushError);
                      // Continue with other products even if one fails
                    }
                  }
                } catch (pushMetricsError) {
                  console.error('Error recording product push metrics:', pushMetricsError);
                  // Continue even if push metrics recording fails
                }
              } else {
                console.warn(`Failed to deduct ${totalImagesProcessed} credits`);
              }
            } catch (error) {
              console.error('Error deducting credits:', error);
              // Continue even if credit deduction fails
            }
          }
        } catch (error) {
          console.error('Error updating statistics:', error);
          setToastProps({
            content: 'Error updating statistics. Please contact support.',
            error: true
          });
        }
      }

      setToastProps({
        content: hasError
          ? `Completed with some errors: ${localUploadedCount} successful, ${localErrorCount} failed`
          : `Successfully pushed ${localUploadedCount} images to Shopify`,
        error: hasError
      });

      // Refund credits for failed push operations
      try {
        // Count how many images failed to be pushed
        const failedImages = localErrorCount;
        if (failedImages > 0) {
          console.log(`Attempting to refund ${failedImages} credits due to Shopify push error`);
          const refundResult = await refundCredits(user.uid, failedImages);
          console.log(`Credit refund result:`, refundResult);
          
          // Add refund information to the error message
          setToastProps({
            content: hasError
              ? `Completed with some errors: ${localUploadedCount} successful, ${localErrorCount} failed. ${failedImages} credits have been refunded.`
              : `Successfully pushed ${localUploadedCount} images to Shopify. ${failedImages} credits have been refunded.`,
            error: hasError
          });
        }
      } catch (refundError) {
        console.error('Error refunding credits:', refundError);
        setToastProps({
          content: hasError
            ? `Completed with some errors: ${localUploadedCount} successful, ${localErrorCount} failed. Additionally, there was an error refunding credits.`
            : `Successfully pushed ${localUploadedCount} images to Shopify. Additionally, there was an error refunding credits.`,
          error: true
        });
      }

      // Clear processed images after successful push
      if (!hasError) {
        setProcessedImages([]);
        await ImageProcessingService.clearProcessedImageState(currentShop.shop);
      }
      setUiLocked(false); // Unlock UI after upload completes or fails
    } catch (error) {
      console.error('Error in handlePushToShopify:', error);
      
      // Refund credits for failed push operations
      try {
        // Count how many images failed to be pushed
        const failedImages = processedImages.length;
        if (failedImages > 0) {
          console.log(`Attempting to refund ${failedImages} credits due to Shopify push error`);
          const refundResult = await refundCredits(user.uid, failedImages);
          console.log(`Credit refund result:`, refundResult);
          
          // Add refund information to the error message
          setToastProps({
            content: `Error pushing images to Shopify: ${error.message}. ${failedImages} credits have been refunded.`,
            error: true
          });
        } else {
          setToastProps({
            content: `Error pushing images to Shopify: ${error.message}`,
            error: true
          });
        }
      } catch (refundError) {
        console.error('Error refunding credits:', refundError);
        setToastProps({
          content: `Error pushing images to Shopify: ${error.message}. Additionally, there was an error refunding credits.`,
          error: true
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!currentShop?.id) {
      console.log('Current shop:', currentShop);
      setToastError(true);
      setToastMessage('Unable to save settings: Shop information not available');
      return;
    }

    // Validate that only one dimension is set
    if (maxWidth && maxHeight) {
      setToastError(true);
      setToastMessage('Please set either max width or max height, not both');
      return;
    }

    // Validate dimension values
    if ((maxWidth && isNaN(maxWidth)) || (maxHeight && isNaN(maxHeight))) {
      setToastError(true);
      setToastMessage('Dimensions must be valid numbers');
      return;
    }

    setSavingSettings(true);
    try {
      await saveImageSettings(currentShop.id, currentShop.shop, {
        format: format, // Use selected format
        compressionLevel,
        maxWidth: maxWidth || null,
        maxHeight: maxHeight || null,
        useSeoNaming
      });
      setToastMessage('Settings saved successfully');
      setToastError(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      setToastError(true);
      setToastMessage('Failed to save settings: ' + error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleMaxWidthChange = useCallback((value) => {
    if (value && maxHeight) {
      // If height is already set, show a toast message
      setToastProps({
        content: 'Only one dimension can be set at a time. Clear max height first to use max width',
        error: false
      });
      setShowToast(true);
      return;
    }
    setMaxWidth(value);
  }, [maxHeight]);

  const handleMaxHeightChange = useCallback((value) => {
    if (value && maxWidth) {
      // If width is already set, show a toast message
      setToastProps({
        content: 'Only one dimension can be set at a time. Clear max width first to use max height',
        error: false
      });
      setShowToast(true);
      return;
    }
    setMaxHeight(value);
  }, [maxWidth]);

  const formatOptions = [
    { label: 'WebP (Best Overall)', value: 'webp' },
    { label: 'JPEG (Wide Support)', value: 'jpeg' },
    { label: 'PNG (Lossless)', value: 'png' }
  ];

  const compressionOptions = [
    { label: 'Low (Best Quality)', value: 'low' },
    { label: 'Medium (Balanced)', value: 'medium' },
    { label: 'High (Smaller Size)', value: 'high' }
  ];

  const handleSelectAll = useCallback(() => {
    // Get all unprocessed product IDs
    const unprocessedProductIds = selectedProducts
      .filter(product => !processedProductIds.has(product.id))
      .map(product => product.id);
      
    // If all unprocessed products are already selected, deselect all
    // Otherwise, select all unprocessed products
    if (selectedItems.length === unprocessedProductIds.length && 
        unprocessedProductIds.every(id => selectedItems.includes(id))) {
      setSelectedItems([]);
    } else {
      setSelectedItems(unprocessedProductIds);
    }
  }, [selectedProducts, selectedItems, processedProductIds]);

  const handleSelectionChange = useCallback(selectedItems => {
    // Get the current set of processed product IDs
    const currentProcessedIds = processedProductIds;
    
    // Filter out any processed products from the selection
    const filteredSelection = selectedItems.filter(itemId => {
      // Check if this ID is in the processed products set
      return !currentProcessedIds.has(itemId);
    });
    
    // If we're trying to select a processed product, show a message
    if (selectedItems.length > filteredSelection.length) {
      console.log('Attempted to select processed products', {
        attempted: selectedItems,
        filtered: filteredSelection,
        processedIds: Array.from(currentProcessedIds)
      });
      
      setToastProps({
        content: 'Processed products cannot be selected again until pushed to Shopify',
        error: false
      });
      setShowToast(true);
    }
    
    setSelectedItems(filteredSelection);
  }, [processedProductIds, setToastProps]);

  const handleViewImages = useCallback(async (product) => {
    console.log('Opening modal for product:', product);
    
    // Create a copy of the product with sorted images
    const productWithSortedImages = {
      ...product,
      images: [...product.images].sort((a, b) => a.position - b.position)
    };
    
    setViewingProduct(productWithSortedImages);
    setShowImagesModal(true);
    setImageSizes({});

    // Fetch sizes for all images
    const sizes = {};
    for (const image of productWithSortedImages.images) {
      try {
        // First try to use existing metadata if available
        if (image.width && image.height && image.size) {
          sizes[image.id] = {
            width: image.width,
            height: image.height,
            size: image.size,
            formattedSize: formatFileSize(image.size)
          };
          continue;
        }

        // Otherwise fetch from the URL
        const response = await fetch(image.src, { method: 'HEAD' });
        const size = response.headers.get('content-length');
        
        sizes[image.id] = {
          width: image.width || null,
          height: image.height || null,
          size: size ? parseInt(size, 10) : null,
          formattedSize: size ? formatFileSize(parseInt(size, 10)) : 'Unknown'
        };
      } catch (error) {
        console.warn('Could not fetch image metadata:', error);
        sizes[image.id] = {
          width: image.width || null,
          height: image.height || null,
          size: null,
          formattedSize: 'Unknown'
        };
      }
    }
    setImageSizes(sizes);
  }, []);

  const renderImageItem = useCallback((image) => {
    const isMarkedForDeletion = imagesToDelete.has(image.id);
    const imageInfo = imageSizes[image.id] || {};

    return (
      <ResourceItem
        id={image.id}
        onClick={() => {
          setSelectedImage(image);
          setShowLargeImageModal(true);
        }}
        media={
          <div style={{ position: 'relative' }}>
            <Thumbnail
              source={image.src}
              alt={image.alt || 'Product image'}
              size="large"
            />
            <div 
              style={{ 
                position: 'absolute', 
                top: '-5px', 
                right: '-5px', 
                zIndex: 1, 
                cursor: 'pointer',
                backgroundColor: isMarkedForDeletion ? 'rgba(255, 0, 0, 0.8)' : 'rgba(0, 128, 0, 0.8)',
                borderRadius: '50%',
                padding: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                color: 'white',
                transition: 'all 0.3s ease'
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleImageDelete(image.id);
              }}
            >
              {isMarkedForDeletion ? '✕' : '✓'}
            </div>
          </div>
        }
      >
        <Stack vertical spacing="tight">
          <Stack.Item>
            <Text variant="bodyMd" as="h3">
              {image.alt || 'Product image'}
            </Text>
          </Stack.Item>
          {imageInfo && (
            <Stack.Item>
              <Stack vertical spacing="extraTight">
                <Text variant="bodySm" color="subdued">
                  {imageInfo.width} × {imageInfo.height} px
                </Text>
                <Text variant="bodySm" color="subdued">
                  Size: {imageInfo.formattedSize}
                </Text>
              </Stack>
            </Stack.Item>
          )}
          {image.variant_ids?.length > 0 && (
            <Stack.Item>
              <Stack spacing="tight">
                {image.variant_ids.map((variantId) => {
                  const variant = viewingProduct.variants.find(v => v.id === variantId);
                  return variant ? (
                    <Badge key={variantId} status="info">
                      {variant.title}
                    </Badge>
                  ) : null;
                })}
              </Stack>
            </Stack.Item>
          )}
        </Stack>
      </ResourceItem>
    );
  }, [imagesToDelete, viewingProduct, imageSizes]);

  const renderImagesModal = useCallback(() => {
    if (!viewingProduct || !showImagesModal) return null;

    return (
      <Modal
        open={showImagesModal}
        onClose={() => {
          setShowImagesModal(false);
          setViewingProduct(null);
        }}
        title={`Images for ${viewingProduct.title}`}
        large
      >
        <Modal.Section>
          <ResourceList
            items={viewingProduct.images || []}
            renderItem={renderImageItem}
          />
        </Modal.Section>
      </Modal>
    );
  }, [viewingProduct, showImagesModal, renderImageItem]);

  const handleExcludeProduct = useCallback((productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  const renderProductList = useCallback((product) => {
    const totalImages = product.images?.length || 0;
    const markedForDeletion = product.images?.filter(img => imagesToDelete.has(img.id)).length || 0;
    const selectedForProcessing = selectedItems.includes(product.id) ? (totalImages - markedForDeletion) : 0;
    
    // Check if this product has already been processed
    const isProcessed = processedProductIds.has(product.id);

    // Get the featured image from the GraphQL data structure
    const featuredImageSrc = product.featuredImage?.url || 
                             (product.images && product.images.length > 0 ? product.images[0].src : '');

    console.log(`Rendering product ${product.id} - processed: ${isProcessed}`);
    
    return (
      <ResourceItem
        id={product.id}
        name={product.title}
        media={
          <div style={{ position: 'relative' }}>
            <Thumbnail
              source={featuredImageSrc}
              alt={product.title}
              size="medium"
            />
            {isProcessed && (
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundColor: 'rgba(0, 128, 0, 0.3)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Badge status="success" size="large">Processed</Badge>
              </div>
            )}
          </div>
        }
        onClick={null}
        verticalAlignment="center"
        selectable={!isProcessed} // Explicitly set selectable to false for processed items
        disabled={isProcessed} // Also disable the item if it's already processed
      >
        <Stack vertical>
          <Stack.Item>
            <Text variant="headingMd">
              {product.title}
              {isProcessed && (
                <span style={{ marginLeft: '10px' }}>
                  <Badge status="success">Processed</Badge>
                </span>
              )}
            </Text>
          </Stack.Item>
          <Stack.Item>
            <Stack distribution="fill">
              <Stack vertical spacing="extraTight">
                <Text variant="bodySm" color="subdued" as="p" alignment="start">
                  {product.images?.length} total images,{' '}
                  {markedForDeletion} marked for deletion,{' '}
                  {selectedForProcessing} selected for processing
                  {isProcessed && <strong style={{color: 'green'}}> (PROCESSED)</strong>}
                </Text>
              </Stack>
              <div style={{ textAlign: 'right', position: 'relative', zIndex: 1 }}>
                <Button 
                  onClick={() => handleViewImages(product)}
                  primary
                  disabled={processing || uiLocked} // Disable button while processing
                >
                  View Images
                </Button>
              </div>
            </Stack>
          </Stack.Item>
        </Stack>
      </ResourceItem>
    );
  }, [imagesToDelete, handleViewImages, selectedItems, processedProductIds, processing, uiLocked]);

  const toggleImageDelete = async (imageId) => {
    try {
      if (!currentShop?.shop) {
        throw new Error('Shop information not available');
      }

      const newImagesToDelete = new Set(imagesToDelete);
      if (newImagesToDelete.has(imageId)) {
        newImagesToDelete.delete(imageId);
      } else {
        newImagesToDelete.add(imageId);
      }
      setImagesToDelete(newImagesToDelete);

      // Get current state
      const currentState = await ImageProcessingService.loadProcessedImageState(currentShop.shop);
      const processedImages = currentState?.processedImages || [];
      
      // Find the image metadata
      const imageToToggle = viewingProduct?.images?.find(img => img.id === imageId);
      if (!imageToToggle) {
        throw new Error('Image not found: ' + imageId);
      }

      // Remove any existing entry for this image
      const filteredImages = processedImages.filter(img => 
        img.originalImage?.id !== imageId
      );

      // Add new entry if marked for deletion
      if (newImagesToDelete.has(imageId)) {
        filteredImages.push({
          originalImage: {
            id: imageToToggle.id,
            product_id: imageToToggle.product_id,
            position: imageToToggle.position,
            variant_ids: imageToToggle.variant_ids || [],
            admin_graphql_api_id: imageToToggle.admin_graphql_api_id
          },
          action: 'delete'
        });
      }

      // Save updated state
      await ImageProcessingService.saveProcessedImageState(currentShop.shop, {
        processedImages: filteredImages,
        shopifyMetadata: currentState?.shopifyMetadata || {}
      });

      // Update local state
      setProcessedImages(filteredImages);

      // Show success message
      setToastMessage(newImagesToDelete.has(imageId) ? 'Image marked for deletion' : 'Image unmarked for deletion');
      setToastError(false);
      setShowToast(true);

    } catch (error) {
      console.error('Error toggling image deletion:', error);
      setToastMessage(error.message || 'Error marking image for deletion');
      setToastError(true);
      setShowToast(true);
      
      // Revert the UI state if save failed
      if (imagesToDelete.has(imageId)) {
        setImagesToDelete(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageId);
          return newSet;
        });
      } else {
        setImagesToDelete(prev => {
          const newSet = new Set(prev);
          newSet.add(imageId);
          return newSet;
        });
      }
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowLargeImageModal(true);
  };

  const renderLargeImageModal = useCallback(() => {
    if (!selectedImage || !showLargeImageModal) return null;

    const imageInfo = imageSizes[selectedImage.id] || {};
    const isMarkedForDeletion = imagesToDelete.has(selectedImage.id);

    return (
      <Modal
        open={showLargeImageModal}
        onClose={() => {
          setShowLargeImageModal(false);
          setSelectedImage(null);
        }}
        title="Image Details"
        large
      >
        <Modal.Section>
          <Stack vertical spacing="loose">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <img
                src={selectedImage.src}
                alt={selectedImage.alt || 'Product image'}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </div>
            <Stack vertical spacing="tight">
              <TextContainer>
                <Stack vertical spacing="extraTight">
                  <Text variant="headingMd">Image Details</Text>
                  <Text>Alt Text: {selectedImage.alt || 'No alt text'}</Text>
                  <Text>Dimensions: {imageSizes[selectedImage.id]?.width} × {imageSizes[selectedImage.id]?.height} px</Text>
                  <Text>File Size: {imageSizes[selectedImage.id]?.formattedSize}</Text>
                  <Text>Position: {selectedImage.position || 'N/A'}</Text>
                  {selectedImage.variant_ids?.length > 0 && (
                    <>
                      <Text>Associated Variants:</Text>
                      <Stack spacing="tight">
                        {selectedImage.variant_ids.map((variantId) => {
                          const variant = viewingProduct.variants.find(v => v.id === variantId);
                          return variant ? (
                            <Badge key={variantId} status="info">
                              {variant.title}
                            </Badge>
                          ) : null;
                        })}
                      </Stack>
                    </>
                  )}
                  {isMarkedForDeletion && (
                    <Banner status="critical">
                      This image will be deleted when changes are pushed to Shopify
                    </Banner>
                  )}
                </Stack>
              </TextContainer>
            </Stack>
          </Stack>
        </Modal.Section>
      </Modal>
    );
  }, [selectedImage, showLargeImageModal, imageSizes, imagesToDelete, viewingProduct]);

  const toggleToast = useCallback(() => setShowToast(false), []);

  const resetProgress = () => {
    setProgress({
      processed: 0,
      total: 0,
      uploaded: 0,
      failed: 0,
      showProgress: false,
      currentOperation: '',
      currentProgress: 0
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  return (
    <Frame>
      <Page
        title="Edit Product Images"
        breadcrumbs={[{ content: 'Products', onAction: () => navigate('/app/products') }]}
      >
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <Stack distribution="equalSpacing" alignment="center">
                <Stack>
                  <div>
                    <Text variant="headingMd" as="h2">
                      {selectedProducts.filter(p => selectedItems.includes(p.id))
                        .reduce((total, product) => 
                          total + (product.images?.filter(img => !imagesToDelete.has(img.id)).length || 0), 
                          0)
                      } images selected for processing
                    </Text>
                    <Text variant="bodySm" color="subdued" as="p">
                      {imagesToDelete.size} images marked for deletion
                    </Text>
                  </div>
                </Stack>
                <ButtonGroup>
                  <Button
                    primary
                    disabled={processing || selectedItems.length === 0 || isUploading}
                    onClick={handleProcessImages}
                    loading={processing}
                  >
                    Process Images
                  </Button>
                  <Button
                    primary={uiLocked && processedImages.length > 0}
                    disabled={processedImages.length === 0 || processing || isUploading}
                    onClick={handlePushToShopify}
                    loading={isUploading}
                  >
                    Push to Shopify
                  </Button>
                </ButtonGroup>
              </Stack>
            </Card>
          </Layout.Section>

          {/* Success banner after processing */}
          {processedImages.length > 0 && (
            <Layout.Section>
              <Card sectioned>
                <Banner
                  status="success"
                  title="Images Processed Successfully"
                >
                  <p>
                    {processedProductIds.size === selectedProducts.length
                      ? 'All products have been processed. You can now select more products to process.'
                      : 'You can now push the processed images to Shopify or select more products to process.'}
                  </p>
                </Banner>
              </Card>
            </Layout.Section>
          )}

          {/* Storage savings and progress bar section */}
          <Layout.Section>
            <Card sectioned>
              <Stack vertical spacing="tight">
                {/* Banner messages moved above the progress bar */}
                {!progress.showProgress && !totalSavings.bytesSaved && processedImages.length === 0 && (
                  <Banner status="info">
                    <Stack vertical spacing="extraTight">
                      <TextStyle>
                        {userGuidanceMessage}
                      </TextStyle>
                    </Stack>
                  </Banner>
                )}

                {totalSavings.bytesSaved > 0 && (
                  <Banner status="success" title="Storage Savings">
                    <Stack vertical spacing="extraTight">
                      <TextStyle variation="strong">
                        Total Space Saved: {formatBytes(totalSavings.bytesSaved)}
                      </TextStyle>
                      <Stack distribution="equalSpacing">
                        <TextStyle>Original: {formatBytes(totalSavings.originalSize)}</TextStyle>
                        <TextStyle>→</TextStyle>
                        <TextStyle>Processed: {formatBytes(totalSavings.processedSize)}</TextStyle>
                        <TextStyle variation="positive">({totalSavings.compressionRatio}% reduction)</TextStyle>
                      </Stack>
                    </Stack>
                  </Banner>
                )}
                {totalSavings.bytesSaved < 0 && (
                  <Banner status="info" title="Storage Increase">
                    <Stack vertical spacing="extraTight">
                      <TextStyle variation="strong">
                        Total Space Increase: {formatBytes(Math.abs(totalSavings.bytesSaved))}
                      </TextStyle>
                      <Stack distribution="equalSpacing">
                        <TextStyle>Original: {formatBytes(totalSavings.originalSize)}</TextStyle>
                        <TextStyle>→</TextStyle>
                        <TextStyle>Processed: {formatBytes(totalSavings.processedSize)}</TextStyle>
                        <TextStyle variation="negative">({Math.abs(totalSavings.compressionRatio)}% increase)</TextStyle>
                      </Stack>
                    </Stack>
                  </Banner>
                )}
                
                {/* Progress bar after the banners */}
                {progress.showProgress && (
                  <>
                    <ProgressBar
                      progress={((progress.processed) / progress.total) * 100}
                      size="small"
                      color="primary"
                    />
                    <Stack distribution="equalSpacing">
                      <TextStyle>
                        {isUploading 
                          ? `${Math.floor(progress.processed/2)} of ${Math.floor(progress.total/2)} images processed`
                          : `${progress.processed} of ${progress.total} processed`
                        }
                      </TextStyle>
                      <TextStyle>
                        {isUploading
                          ? `${Math.floor(progress.uploaded/2)} successful, ${Math.floor(progress.failed/2)} failed`
                          : `${progress.uploaded} successful, ${progress.failed} failed`
                        }
                      </TextStyle>
                    </Stack>
                    {progress.currentOperation && (
                      <TextStyle variation="subdued">
                        {progress.currentOperation}
                      </TextStyle>
                    )}
                  </>
                )}
              </Stack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card title="Image Settings" sectioned>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    label="Format"
                    options={formatOptions}
                    value={format}
                    onChange={setFormat}
                    disabled={processing || savingSettings || isUploading || uiLocked}
                  />
                  <Select
                    label="Compression Level"
                    options={compressionOptions}
                    value={compressionLevel}
                    onChange={setCompressionLevel}
                    disabled={processing || savingSettings || isUploading || uiLocked}
                  />
                </FormLayout.Group>
                <Text variant="bodyMd" color="subdued" as="p" alignment="start">
                  Optional: Set either max width OR max height to resize images while maintaining aspect ratio. Setting one will automatically clear the other.
                </Text>
                <FormLayout.Group>
                  <TextField
                    label="Max Width (px)"
                    type="number"
                    value={maxWidth}
                    onChange={handleMaxWidthChange}
                    disabled={processing || savingSettings || isUploading || uiLocked}
                    placeholder="Leave empty for original width"
                    min={1}
                    helpText={maxHeight ? "Clear max height first to use max width" : "Set max width while maintaining aspect ratio"}
                    monospaced
                    error={undefined}
                    onBlur={() => {
                      if (maxWidth && maxHeight) {
                        setMaxWidth('');
                      }
                    }}
                  />
                  <TextField
                    label="Max Height (px)"
                    type="number"
                    value={maxHeight}
                    onChange={handleMaxHeightChange}
                    disabled={processing || savingSettings || isUploading || uiLocked}
                    placeholder="Leave empty for original height"
                    min={1}
                    helpText={maxWidth ? "Clear max width first to use max height" : "Set max height while maintaining aspect ratio"}
                    monospaced
                    error={undefined}
                    onBlur={() => {
                      if (maxWidth && maxHeight) {
                        setMaxHeight('');
                      }
                    }}
                  />
                </FormLayout.Group>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Checkbox
                    label="SEO Compatible"
                    checked={useSeoNaming}
                    onChange={setUseSeoNaming}
                    disabled={processing || savingSettings || isUploading || uiLocked}
                  />
                  <Button 
                    plain
                    icon={<span>?</span>}
                    onClick={() => {
                      setToastMessage(
                        'When enabled, images will be renamed using the product title in an SEO-friendly format. ' +
                        'For example: "Modern LED Light" becomes "modern-led-light", "modern-led-light-1", etc.'
                      );
                      setToastError(false);
                      setShowToast(true);
                    }}
                    disabled={processing || uiLocked}
                  />
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <Button
                    onClick={handleSaveSettings}
                    loading={savingSettings}
                    disabled={processing || isUploading || uiLocked}
                  >
                    Save Settings
                  </Button>
                </div>
              </FormLayout>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <Card.Section>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <Text variant="headingMd">Products</Text>
                  <Button onClick={handleSelectAll}>
                    {selectedItems.length === selectedProducts.filter(p => !processedProductIds.has(p.id)).length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </Card.Section>
              {loading ? (
                <Card.Section>
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spinner accessibilityLabel="Loading products" size="large" />
                    <div style={{ marginTop: '1rem' }}>Loading products...</div>
                  </div>
                </Card.Section>
              ) : (
                <ResourceList
                  resourceName={{ singular: 'Product', plural: 'Products' }}
                  items={selectedProducts.map(product => {
                    // Add disabled flag directly to each item
                    const isProcessed = processedProductIds.has(product.id);
                    console.log(`Product ${product.id} - isProcessed: ${isProcessed}`);
                    return {
                      ...product,
                      disabled: isProcessed,
                      selectable: !isProcessed
                    };
                  })}
                  renderItem={renderProductList}
                  selectedItems={selectedItems}
                  onSelectionChange={handleSelectionChange}
                  selectable
                  promotedBulkActions={[
                    {
                      content: 'Process Selected',
                      onAction: () => handleProcessImages(),
                      disabled: processing || selectedItems.length === 0 || isUploading
                    }
                  ]}
                  filterControl={(
                    <div style={{ padding: '8px 0' }}>
                      <Text variant="bodySm" color="subdued">
                        {processedProductIds.size} of {selectedProducts.length} products processed
                      </Text>
                    </div>
                  )}
                  // This is a critical fix to prevent processed products from being selectable
                  resolveItemId={item => {
                    // This ensures that processed products cannot be selected
                    // by giving them a different ID format that won't match the selectedItems array
                    const isProcessed = processedProductIds.has(item.id);
                    return isProcessed ? `processed-${item.id}` : item.id;
                  }}
                />
              )}
            </Card>
          </Layout.Section>

          {viewingProduct && showImagesModal && (
            <Modal
              open={showImagesModal}
              onClose={() => {
                setShowImagesModal(false);
                setViewingProduct(null);
              }}
              title={`Images for ${viewingProduct.title}`}
              large
            >
              <Modal.Section>
                <ResourceList
                  items={viewingProduct.images || []}
                  renderItem={renderImageItem}
                />
              </Modal.Section>
            </Modal>
          )}

          {selectedImage && showLargeImageModal && (
            <Modal
              open={showLargeImageModal}
              onClose={() => {
                setShowLargeImageModal(false);
                setSelectedImage(null);
              }}
              title="Image Details"
              large
            >
              <Modal.Section>
                <Stack vertical spacing="loose">
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <img
                      src={selectedImage.src}
                      alt={selectedImage.alt || 'Product image'}
                      style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                    />
                  </div>
                  <Stack vertical spacing="tight">
                    <TextContainer>
                      <Stack vertical spacing="extraTight">
                        <Text variant="headingMd">Image Details</Text>
                        <Text>Alt Text: {selectedImage.alt || 'No alt text'}</Text>
                        <Text>Dimensions: {imageSizes[selectedImage.id]?.width} × {imageSizes[selectedImage.id]?.height} px</Text>
                        <Text>File Size: {imageSizes[selectedImage.id]?.formattedSize}</Text>
                        <Text>Position: {selectedImage.position || 'N/A'}</Text>
                        {selectedImage.variant_ids?.length > 0 && (
                          <>
                            <Text>Associated Variants:</Text>
                            <Stack spacing="tight">
                              {selectedImage.variant_ids.map((variantId) => {
                                const variant = viewingProduct.variants.find(v => v.id === variantId);
                                return variant ? (
                                  <Badge key={variantId} status="info">
                                    {variant.title}
                                  </Badge>
                                ) : null;
                              })}
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </TextContainer>
                  </Stack>
                </Stack>
              </Modal.Section>
            </Modal>
          )}

          {showToast && (
            <Toast
              content={toastMessage}
              error={toastError}
              onDismiss={() => setShowToast(false)}
            />
          )}
        </Layout>
      </Page>
    </Frame>
  );
}

export default BulkEditImages;
